export const BENCHMARK_SCHEMA_VERSION = "artetra-gallery-benchmark-v1";
export const DEFAULT_FRAME_CAPACITY = 600;
export const MAX_BENCHMARK_ERRORS = 25;

export type BenchmarkMetric = number | "unavailable";

export type BenchmarkResourceSnapshot = {
  geometries: BenchmarkMetric;
  textures: BenchmarkMetric;
  programs: BenchmarkMetric;
  calls?: BenchmarkMetric;
  triangles?: BenchmarkMetric;
  points?: BenchmarkMetric;
  lines?: BenchmarkMetric;
};

export type BenchmarkResult = {
  schemaVersion: typeof BENCHMARK_SCHEMA_VERSION;
  scenarioId: string;
  fixtureRevision: string;
  startedAtEpochMs: number;
  durationMs: number;
  milestones: Record<string, number>;
  frames: {
    sampleCount: number;
    p50Ms: BenchmarkMetric;
    p95Ms: BenchmarkMetric;
    maxMs: BenchmarkMetric;
    fpsFromP50: BenchmarkMetric;
    longFramesOver50Ms: number;
    longFramesOver100Ms: number;
    droppedSamples: number;
  };
  contextLoss: {
    total: number;
    unexpected: number;
    duringTeardown: number;
  };
  resources: {
    loaded: BenchmarkResourceSnapshot | null;
    teardown: BenchmarkResourceSnapshot | null;
    balance: BenchmarkResourceSnapshot | null;
  };
  errors: Array<{ code: string; path: string }>;
};

const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001F\u007F]/;
const SAFE_ERROR_CODE_PATTERN = /^[A-Z0-9_]{1,64}$/;
const SAFE_SCENARIO_PATTERN = /^(light|representative|heavy)-v[1-9][0-9]{0,2}$/;

const finiteMetric = (value: unknown): BenchmarkMetric => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : "unavailable";
};

export const calculatePercentile = (values: number[], percentile: number): BenchmarkMetric => {
  const finiteValues = values.filter((value) => Number.isFinite(value));
  if (finiteValues.length === 0) return "unavailable";

  const sortedValues = [...finiteValues].sort((left, right) => left - right);
  const boundedPercentile = Math.max(0, Math.min(1, percentile));
  const rank = (sortedValues.length - 1) * boundedPercentile;
  const lowerIndex = Math.floor(rank);
  const upperIndex = Math.ceil(rank);
  const lower = sortedValues[lowerIndex];
  const upper = sortedValues[upperIndex];

  if (lowerIndex === upperIndex) return lower;
  return lower + (upper - lower) * (rank - lowerIndex);
};

export class BoundedFrameSampler {
  private readonly samples: number[] = [];
  public droppedSamples = 0;

  constructor(private readonly capacity = DEFAULT_FRAME_CAPACITY) {
    if (!Number.isInteger(capacity) || capacity < 1 || capacity > 10_000) {
      throw new Error("Frame capacity must be an integer between 1 and 10000.");
    }
  }

  public record(frameMs: number) {
    if (!Number.isFinite(frameMs) || frameMs <= 0) return;

    if (this.samples.length === this.capacity) {
      this.samples.shift();
      this.droppedSamples += 1;
    }
    this.samples.push(frameMs);
  }

  public values() {
    return [...this.samples];
  }
}

export const isBenchmarkScenarioName = (value: unknown): value is string => {
  return typeof value === "string" && SAFE_SCENARIO_PATTERN.test(value);
};

export const sanitizeBenchmarkPath = (value: unknown): string => {
  if (typeof value !== "string" || value.length === 0 || value.length > 512) return "";
  if (CONTROL_CHARACTER_PATTERN.test(value) || !value.startsWith("/") || value.startsWith("//")) return "";

  const queryIndex = value.indexOf("?");
  const fragmentIndex = value.indexOf("#");
  const boundaryIndexes = [queryIndex, fragmentIndex].filter((index) => index >= 0);
  const boundary = boundaryIndexes.length > 0 ? Math.min(...boundaryIndexes) : value.length;
  const path = value.slice(0, boundary);

  return path.length > 0 && path.length <= 256 ? path : "";
};

export const createResourceBalance = (
  loaded: BenchmarkResourceSnapshot,
  teardown: BenchmarkResourceSnapshot,
): BenchmarkResourceSnapshot => {
  const keys: Array<keyof BenchmarkResourceSnapshot> = [
    "geometries",
    "textures",
    "programs",
    "calls",
    "triangles",
    "points",
    "lines",
  ];
  const balance: Partial<BenchmarkResourceSnapshot> = {};

  keys.forEach((key) => {
    const loadedValue = loaded[key];
    const teardownValue = teardown[key];
    if (typeof loadedValue === "number" && typeof teardownValue === "number") {
      balance[key] = teardownValue - loadedValue;
      return;
    }
    if (loadedValue !== undefined || teardownValue !== undefined) {
      balance[key] = "unavailable";
    }
  });

  return balance as BenchmarkResourceSnapshot;
};

type GalleryBenchmarkSessionOptions = {
  scenarioId: string;
  fixtureRevision: string;
  now?: () => number;
  frameCapacity?: number;
};

export class GalleryBenchmarkSession {
  private readonly startedAtEpochMs: number;
  private readonly now: () => number;
  private readonly frames: BoundedFrameSampler;
  private readonly milestones: Record<string, number> = {};
  private readonly errors: Array<{ code: string; path: string }> = [];
  private contextLossTotal = 0;
  private contextLossUnexpected = 0;
  private contextLossDuringTeardown = 0;
  private teardownStarted = false;
  private loadedResources: BenchmarkResourceSnapshot | null = null;
  private teardownResources: BenchmarkResourceSnapshot | null = null;
  private terminalResult: BenchmarkResult | null = null;

  constructor(private readonly options: GalleryBenchmarkSessionOptions) {
    if (!isBenchmarkScenarioName(options.scenarioId)) {
      throw new Error("Invalid benchmark scenario name.");
    }
    if (!/^[A-Za-z0-9._-]{1,64}$/.test(options.fixtureRevision)) {
      throw new Error("Invalid fixture revision.");
    }

    this.now = options.now || (() => performance.now());
    this.startedAtEpochMs = this.now();
    this.frames = new BoundedFrameSampler(options.frameCapacity);
  }

  public mark(name: string) {
    if (this.terminalResult || !/^[A-Za-z][A-Za-z0-9]{0,63}$/.test(name)) return;
    if (this.milestones[name] !== undefined) return;
    this.milestones[name] = Math.max(0, this.now() - this.startedAtEpochMs);
  }

  public recordFrame(frameMs: number) {
    if (this.terminalResult) return;
    this.frames.record(frameMs);
  }

  public recordContextLoss() {
    if (this.terminalResult) return;
    this.contextLossTotal += 1;
    if (this.teardownStarted) {
      this.contextLossDuringTeardown += 1;
    } else {
      this.contextLossUnexpected += 1;
    }
  }

  public recordError(code: string, path = "") {
    if (this.terminalResult || this.errors.length >= MAX_BENCHMARK_ERRORS) return;
    if (!SAFE_ERROR_CODE_PATTERN.test(code)) return;
    this.errors.push({ code, path: sanitizeBenchmarkPath(path) });
  }

  public recordResourceSnapshot(phase: "loaded" | "teardown", snapshot: BenchmarkResourceSnapshot) {
    if (this.terminalResult) return;
    const normalized = {
      geometries: finiteMetric(snapshot.geometries),
      textures: finiteMetric(snapshot.textures),
      programs: finiteMetric(snapshot.programs),
      calls: finiteMetric(snapshot.calls),
      triangles: finiteMetric(snapshot.triangles),
      points: finiteMetric(snapshot.points),
      lines: finiteMetric(snapshot.lines),
    };
    if (phase === "loaded") this.loadedResources = normalized;
    if (phase === "teardown") this.teardownResources = normalized;
  }

  public beginTeardown() {
    if (this.terminalResult || this.teardownStarted) return;
    this.teardownStarted = true;
    this.mark("teardownStartedMs");
  }

  public completeTeardown() {
    if (this.terminalResult) return;
    this.mark("teardownCompletedMs");
  }

  public getState() {
    return {
      scenarioId: this.options.scenarioId,
      fixtureRevision: this.options.fixtureRevision,
      finalized: this.terminalResult !== null,
      milestones: { ...this.milestones },
    };
  }

  public finalize(): BenchmarkResult {
    if (this.terminalResult) return this.terminalResult;

    const frameValues = this.frames.values();
    const p50Ms = calculatePercentile(frameValues, 0.5);
    const p95Ms = calculatePercentile(frameValues, 0.95);
    const maxMs = frameValues.length > 0 ? Math.max(...frameValues) : "unavailable";
    const fpsFromP50 = typeof p50Ms === "number" && p50Ms > 0 ? 1000 / p50Ms : "unavailable";

    this.terminalResult = Object.freeze({
      schemaVersion: BENCHMARK_SCHEMA_VERSION,
      scenarioId: this.options.scenarioId,
      fixtureRevision: this.options.fixtureRevision,
      startedAtEpochMs: this.startedAtEpochMs,
      durationMs: Math.max(0, this.now() - this.startedAtEpochMs),
      milestones: Object.freeze({ ...this.milestones }),
      frames: Object.freeze({
        sampleCount: frameValues.length,
        p50Ms,
        p95Ms,
        maxMs,
        fpsFromP50,
        longFramesOver50Ms: frameValues.filter((value) => value > 50).length,
        longFramesOver100Ms: frameValues.filter((value) => value > 100).length,
        droppedSamples: this.frames.droppedSamples,
      }),
      contextLoss: Object.freeze({
        total: this.contextLossTotal,
        unexpected: this.contextLossUnexpected,
        duringTeardown: this.contextLossDuringTeardown,
      }),
      resources: Object.freeze({
        loaded: this.loadedResources ? Object.freeze({ ...this.loadedResources }) : null,
        teardown: this.teardownResources ? Object.freeze({ ...this.teardownResources }) : null,
        balance: this.loadedResources && this.teardownResources
          ? Object.freeze(createResourceBalance(this.loadedResources, this.teardownResources))
          : null,
      }),
      errors: Object.freeze(this.errors.map((error) => Object.freeze({ ...error }))),
    }) as BenchmarkResult;

    return this.terminalResult;
  }
}
type GalleryBenchmarkBridge = {
  getState: () => ReturnType<GalleryBenchmarkSession["getState"]>;
  finalize: () => BenchmarkResult;
};

declare global {
  interface Window {
    __ARTETRA_GALLERY_BENCHMARK__?: GalleryBenchmarkBridge;
  }
}

type RendererInfo = {
  memory?: { geometries?: number; textures?: number };
  programs?: unknown[] | null;
  render?: { calls?: number; triangles?: number; points?: number; lines?: number };
};

let activeSession: GalleryBenchmarkSession | null = null;

export const getActiveGalleryBenchmark = () => activeSession;

export const startGalleryBenchmark = (
  search: string,
  buildFlag: string | undefined,
  fixtureRevision: string,
): GalleryBenchmarkSession | null => {
  if (buildFlag !== "true") return null;

  const scenarioId = new URLSearchParams(search).get("benchmark");
  if (!isBenchmarkScenarioName(scenarioId)) return null;

  activeSession = new GalleryBenchmarkSession({ scenarioId, fixtureRevision });
  activeSession.mark("routeMountedMs");
  window.__ARTETRA_GALLERY_BENCHMARK__ = {
    getState: () => activeSession?.getState() || {
      scenarioId,
      fixtureRevision,
      finalized: true,
      milestones: {},
    },
    finalize: () => {
      if (!activeSession) throw new Error("Gallery benchmark session is unavailable.");
      return activeSession.finalize();
    },
  };

  return activeSession;
};

export const markGalleryBenchmark = (name: string) => activeSession?.mark(name);

export const recordGalleryBenchmarkFrame = (frameMs: number) => activeSession?.recordFrame(frameMs);

export const recordGalleryBenchmarkError = (code: string, path = "") => {
  activeSession?.recordError(code, path);
};

export const beginGalleryBenchmarkTeardown = () => activeSession?.beginTeardown();

export const completeGalleryBenchmarkTeardown = () => activeSession?.completeTeardown();

export const recordGalleryBenchmarkRendererSnapshot = (
  phase: "loaded" | "teardown",
  info: RendererInfo,
) => {
  activeSession?.recordResourceSnapshot(phase, {
    geometries: info.memory?.geometries,
    textures: info.memory?.textures,
    programs: info.programs?.length,
    calls: info.render?.calls,
    triangles: info.render?.triangles,
    points: info.render?.points,
    lines: info.render?.lines,
  } as BenchmarkResourceSnapshot);
};

export const attachGalleryBenchmarkContextListener = (canvas: HTMLCanvasElement) => {
  if (!activeSession) return () => undefined;

  const handleContextLost = (event: Event) => {
    event.preventDefault();
    activeSession?.recordContextLoss();
  };
  canvas.addEventListener("webglcontextlost", handleContextLost);
  return () => canvas.removeEventListener("webglcontextlost", handleContextLost);
};

export const resetGalleryBenchmarkForTests = () => {
  activeSession = null;
  if (typeof window !== "undefined") delete window.__ARTETRA_GALLERY_BENCHMARK__;
};
